/**
 * PositionTrackerOverlayContent (LRG.16) — UI for PositionMutationTracker.
 *
 * Shows every code path that writes to `player.position.{x,y}` with
 * per-source counters + recent mutation log. Defaults OFF so prod has
 * zero overhead; checkbox flips the singleton's `enabled` flag.
 *
 * Use case: enable the tool + move around. The source table reveals
 * exactly which code paths write player.position — catches unexpected
 * mutators of the client-owned position — e.g., "EntitySystem.ts:298
 * (noPredicted) — 47 writes in 10s."
 *
 * data-testid: position-tracker-overlay + per-source row testid +
 * data-mutations-last-10s attr for Playwright assertion.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { PositionMutationTracker, type PositionMutationStats } from '../../../diagnostics/PositionMutationTracker';
import { CopyButton } from '../CopyButton';

const POLL_MS = 500;

export const PositionTrackerOverlayContent: React.FC = () => {
  const [stats, setStats] = useState<PositionMutationStats>(() => PositionMutationTracker.getStats());

  useEffect(() => {
    const id = setInterval(() => setStats(PositionMutationTracker.getStats()), POLL_MS);
    return () => clearInterval(id);
  }, []);

  const toggleEnabled = useCallback(() => {
    PositionMutationTracker.setEnabled(!stats.enabled);
    setStats(PositionMutationTracker.getStats());
  }, [stats.enabled]);

  const clear = useCallback(() => {
    PositionMutationTracker.clear();
    setStats(PositionMutationTracker.getStats());
  }, []);

  const copyText = () => {
    const lines = [
      `POSITION MUTATION TRACKER @ ${new Date().toISOString()}`,
      `enabled:        ${stats.enabled}`,
      `instrumented:   ${stats.instrumented}`,
      `total:          ${stats.totalMutations}`,
      `last10s:        ${stats.mutationsLast10s}`,
      '',
      `Top sources (last 10s):`,
      ...stats.perSourceLast10s.slice(0, 10).map(s => `  ${s.count.toString().padStart(4)} · ${s.source}`),
      '',
      `Top sources (cumulative):`,
      ...stats.perSourceTotal.slice(0, 10).map(s => `  ${s.count.toString().padStart(4)} · ${s.source}`),
      '',
      `Recent mutations (last 20):`,
      ...stats.recentMutations.map(m => `  ${new Date(m.at).toISOString()}  ${m.axis}: ${m.prevValue.toFixed(1)}→${m.newValue.toFixed(1)} (Δ${m.delta.toFixed(1)})  ${m.source}`),
    ];
    return lines.join('\n');
  };

  const headerStyle: React.CSSProperties = {
    color: '#7af',
    marginBottom: 4,
    letterSpacing: '0.05em',
    fontSize: 10,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const toggleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 6px',
    borderRadius: 3,
    border: `1px solid ${stats.enabled ? '#42d77d' : '#666'}`,
    background: stats.enabled ? 'rgba(66,215,125,0.10)' : 'rgba(255,255,255,0.04)',
    color: stats.enabled ? '#42d77d' : '#aaa',
    fontSize: 10,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 6,
  };

  const sectionTitleStyle: React.CSSProperties = {
    color: '#7af',
    margin: '6px 0 2px',
    fontSize: 9,
    letterSpacing: '0.05em',
    fontWeight: 600,
  };

  const sourceRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '40px 1fr',
    columnGap: 6,
    fontSize: 10,
    padding: '1px 0',
  };

  return (
    <div
      data-testid="position-tracker-overlay"
      data-enabled={String(stats.enabled)}
      data-instrumented={String(stats.instrumented)}
      data-mutations-last-10s={stats.mutationsLast10s}
      data-mutations-total={stats.totalMutations}
      style={{
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 11,
        padding: '6px 8px',
        userSelect: 'none',
        lineHeight: 1.4,
      }}
    >
      <div style={headerStyle}>
        <span>POSITION TRACKER · player.position writes</span>
        <CopyButton getText={copyText} />
      </div>

      <label
        style={toggleStyle}
        data-testid="position-tracker-toggle"
      >
        <input
          type="checkbox"
          checked={stats.enabled}
          onChange={toggleEnabled}
          data-testid="position-tracker-checkbox"
          style={{ accentColor: stats.enabled ? '#42d77d' : '#666', cursor: 'pointer' }}
        />
        <span>{stats.enabled ? '✅ TRACKING' : '⏸ paused (default)'}</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={clear}
          data-testid="position-tracker-clear"
          style={{
            padding: '1px 6px',
            fontSize: 9,
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 3,
            background: 'transparent',
            color: '#aaa',
            cursor: 'pointer',
          }}
          title="Clear all counters + recent log"
        >
          clear
        </button>
      </label>

      <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>
        Total: <span style={{ color: '#bbb' }}>{stats.totalMutations}</span> ·
        Last 10s: <span style={{ color: stats.mutationsLast10s > 0 ? '#fa5' : '#42d77d' }}>{stats.mutationsLast10s}</span> ·
        Instrumented: <span style={{ color: stats.instrumented ? '#42d77d' : '#ff5f5f' }}>{String(stats.instrumented)}</span>
      </div>

      {!stats.instrumented && (
        <div style={{ fontSize: 9, color: '#fa5', marginBottom: 4 }}>
          ⚠ Not instrumented — player.position hasn't been wired yet. Make sure you've spawned.
        </div>
      )}

      {stats.perSourceLast10s.length > 0 && (
        <>
          <div style={sectionTitleStyle}>TOP SOURCES · last 10s</div>
          {stats.perSourceLast10s.slice(0, 8).map(s => (
            <div
              key={`w-${s.source}`}
              style={sourceRowStyle}
              data-testid="position-tracker-source-row"
              data-source={s.source}
              data-count={s.count}
            >
              <span style={{ color: '#fa5', textAlign: 'right' }}>{s.count}</span>
              <span style={{ color: '#ccc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.source}>{s.source}</span>
            </div>
          ))}
        </>
      )}

      {stats.recentMutations.length > 0 && (
        <>
          <div style={sectionTitleStyle}>RECENT · last {Math.min(8, stats.recentMutations.length)}</div>
          {stats.recentMutations.slice(-8).reverse().map((m, i) => (
            <div key={`r-${i}-${m.at}`} style={{ fontSize: 9, color: '#999', padding: '1px 0' }}>
              <span style={{ color: '#7af' }}>{m.axis}</span>{' '}
              <span style={{ color: '#888' }}>{m.prevValue.toFixed(0)}→{m.newValue.toFixed(0)}</span>{' '}
              <span style={{ color: Math.abs(m.delta) > 10 ? '#fa5' : '#bbb' }}>Δ{m.delta.toFixed(1)}</span>{' '}
              <span style={{ color: '#666' }}>{m.source.slice(0, 40)}</span>
            </div>
          ))}
        </>
      )}

      {stats.enabled && stats.mutationsLast10s === 0 && (
        <div style={{ fontSize: 9, color: '#42d77d', marginTop: 6 }}>
          🟢 No writes in last 10s. Either the player isn't moving (idle) or
          all mutators are correctly gated.
        </div>
      )}
    </div>
  );
};
